import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import type { ApiClientError, ApiSuccess } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { PageHeader } from '@/components/efs/PageHeader';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

interface OpeningBalanceDto {
  monthly_batch_id: string;
  opening_wip_kg: string | null;
  opening_material_inventory_kg: string | null;
  note: string | null;
  confirmed_at: string | null;
  confirmed_by: string | null;
}

const NS = 'batches';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

const buildOpeningBalanceSchema = (t: TFn) => {
  const kgString = z
    .string()
    .min(1)
    .regex(/^\d+(\.\d{1,3})?$/, t('efs.monthly-batches.opening-balance.validation.kg_format'));
  return z.object({
    opening_wip_kg: kgString,
    opening_material_inventory_kg: kgString,
    note: z.string().max(2000).optional(),
  });
};

type OpeningBalanceInput = z.infer<ReturnType<typeof buildOpeningBalanceSchema>>;

const fetchOpeningBalance = async (id: string): Promise<OpeningBalanceDto> => {
  const res = await apiClient.get<ApiSuccess<OpeningBalanceDto>>(
    `/monthly-batches/${id}/opening-balance`,
  );
  return res.data.data;
};

const saveOpeningBalance = async (
  id: string,
  body: OpeningBalanceInput,
): Promise<OpeningBalanceDto> => {
  const res = await apiClient.put<ApiSuccess<OpeningBalanceDto>>(
    `/monthly-batches/${id}/opening-balance`,
    body,
  );
  return res.data.data;
};

export const OpeningBalancePage = () => {
  const { t } = useTranslation([NS, 'common']);
  const { batchId } = useParams<{ batchId: string }>();
  const user = useCurrentUser();
  const qc = useQueryClient();

  const canEdit = hasPermission(user, 'batch.manage');

  const openingBalanceSchema = useMemo(() => buildOpeningBalanceSchema(t), [t]);

  const query = useQuery<OpeningBalanceDto, ApiClientError>({
    queryKey: ['monthly-batches', 'opening-balance', batchId],
    queryFn: () => fetchOpeningBalance(batchId!),
    enabled: !!batchId,
  });

  const form = useForm<OpeningBalanceInput>({
    resolver: zodResolver(openingBalanceSchema),
    defaultValues: {
      opening_wip_kg: '',
      opening_material_inventory_kg: '',
      note: '',
    },
  });

  // Populate the form once the API returns existing values.
  useEffect(() => {
    if (query.data) {
      form.reset({
        opening_wip_kg: query.data.opening_wip_kg ?? '',
        opening_material_inventory_kg: query.data.opening_material_inventory_kg ?? '',
        note: query.data.note ?? '',
      });
    }
  }, [query.data, form]);

  const mutation = useMutation<OpeningBalanceDto, ApiClientError, OpeningBalanceInput>({
    mutationFn: (body) => saveOpeningBalance(batchId!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['monthly-batches', 'opening-balance', batchId] });
      qc.invalidateQueries({ queryKey: ['monthly-batches', 'detail', batchId] });
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    await mutation.mutateAsync(values);
  });

  const confirmed = !!query.data?.confirmed_at;
  const disabled = !canEdit || confirmed || mutation.isPending;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.monthly-batches.opening-balance.title')}
        subtitle={t('efs.monthly-batches.opening-balance.subtitle')}
      />

      {!canEdit ? (
        <p className="text-sm text-efs-text-muted">
          {t('efs.monthly-batches.opening-balance.messages.permission_denied')}
        </p>
      ) : null}

      <p className="text-xs text-efs-text-muted">
        {t('efs.monthly-batches.opening-balance.warning_first_month')}
      </p>

      {query.isLoading ? (
        <p className="text-sm text-efs-text-muted">
          {t('efs.common.messages.loading', { ns: 'common' })}
        </p>
      ) : (
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4 efs-panel p-6" noValidate>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="opening_wip_kg"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('efs.monthly-batches.opening-balance.fields.opening_wip_kg')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0.000"
                        disabled={disabled}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="opening_material_inventory_kg"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t(
                        'efs.monthly-batches.opening-balance.fields.opening_material_inventory_kg',
                      )}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0.000"
                        disabled={disabled}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.monthly-batches.opening-balance.fields.note')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} disabled={disabled} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {confirmed ? (
              <div className="rounded-md border border-efs-border bg-efs-bg-subtle p-3 text-xs text-efs-text-muted">
                <p>
                  <span className="font-medium">
                    {t('efs.monthly-batches.opening-balance.fields.confirmed_at')}:
                  </span>{' '}
                  {query.data?.confirmed_at
                    ? new Date(query.data.confirmed_at).toLocaleString()
                    : '—'}
                </p>
                <p>
                  <span className="font-medium">
                    {t('efs.monthly-batches.opening-balance.fields.confirmed_by')}:
                  </span>{' '}
                  {query.data?.confirmed_by ?? '—'}
                </p>
              </div>
            ) : null}

            <div className="flex justify-end">
              <Button type="submit" disabled={disabled}>
                {mutation.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.monthly-batches.opening-balance.actions.save')}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </div>
  );
};

export default OpeningBalancePage;
